import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { useParams, useNavigate, Outlet, useLocation } from "react-router-dom";
import Layout from "../../../Layout";
import apiService from "../../../services/service";
import API_ROUTES_V2 from "../../../api-routes-v2";
import { Skeleton } from "primereact/skeleton";
import { Card } from "primereact/card";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import TenantSidebar from "./TenantSidebar";
import AssignUserModal from "../modals/AssignUserModal.tsx";
import UnassignModal from "../modals/UnassignModal.tsx";
import ToastMessage from "../../../components/ToastMessage";
import useTenants from "../useTenants";
import "./TenantInformationLayout.scss";
import { useStoreSelector } from "~/lib/store/hooks";
import { tenantOnboardingStore } from "../onboarding/store/tenantOnboardingStore";
import { useQueryClient } from "@tanstack/react-query";

const TenantInformationContext = createContext();

const TenantInformationLayout = ({ children } = {}) => {
  const toastRef = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchTenantUsers, updateUserTenants } = useTenants();
  const queryClient = useQueryClient();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [displayAssignUserDialog, setDisplayAssignUserDialog] = useState(false);
  const [displayUnassignUserDialog, setDisplayUnassignUserDialog] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [refetchUsersCallback, setRefetchUsersCallback] = useState(null);
  const [unassignLoading, setUnassignLoading] = useState(false);

  // Determine active section from URL path
  const getActiveSectionFromPath = () => {
    if (location.pathname.includes("/users")) return "assigned-users";
    if (location.pathname.includes("/admin-information")) return "admin-info";
    if (location.pathname.includes("/customer-portfolio"))
      return "customer-portfolio";
    return "info";
  };

  const [activeSection, setActiveSection] = useState(
    getActiveSectionFromPath(),
  );

  useEffect(() => {
    setActiveSection(getActiveSectionFromPath());
  }, [location.pathname]);

  useEffect(() => {
    fetchTenantDetails();
  }, [id]);

  const fetchTenantDetails = async () => {
    try {
      setLoading(true);
      const url = API_ROUTES_V2.FETCH_TENANT.replace("{id}", id);
      const response = await apiService(url, "GET");
      const tenantData = response?.data || response;
      setTenant(tenantData);
    } catch (error) {
      console.error("Error fetching tenant:", error);
      toastRef?.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch tenant details",
        life: 3000,
        icon: "pi pi-times-circle",
      });
      setTimeout(() => navigate(`/tenants/${id}`), 2000);
    } finally {
      setLoading(false);
    }
  };

  const generalInformation = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.generalInformation,
  );
  useEffect(() => {
    setTenant(generalInformation);
  }, [generalInformation]);

  const getBreadcrumbLabel = () => {
    if (!tenant) return "Loading...";
    const countryName = tenant.country ? getName(tenant.country) : "";
    const countryFlag = tenant.country ? (
      <ReactCountryFlag
        countryCode={tenant.country}
        svg
        style={{ width: "16px", height: "16px", marginRight: "4px" }}
      />
    ) : null;

    return (
      <span
        onClick={() => navigate(`/tenants/${id}`)}
        style={{ cursor: "pointer", textDecoration: "underline" }}
        title="Go back to tenant details"
      >
        {tenant.tenant_name}
        {countryName && (
          <span style={{ marginLeft: "8px" }}>
            ({countryFlag}
            {countryName})
          </span>
        )}
      </span>
    );
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (section === "info") {
      navigate(`/tenants/${id}/info`);
    } else if (section === "assigned-users") {
      navigate(`/tenants/${id}/users`);
    } else if (section === "admin-info") {
      navigate(`/tenants/${id}/admin-information`);
    } else if (section === "customer-portfolio") {
      navigate(`/tenants/${id}/customer-portfolio`);
    }
  };

  const handleNavigateBack = () => {
    navigate(`/tenants/${id}`);
  };

  const handleAssignUser = () => {
    setDisplayAssignUserDialog(true);
    // Modal will handle fetching users via its own useEffect
  };

  const handleUnAssignUser = (user) => {
    setSelectedUser(user);
    setDisplayUnassignUserDialog(true);
  };

  const handleViewHistory = () => {
    // TODO: Implement view history functionality
    console.log("View History clicked");
  };

  const handleRefetchUsers = async () => {
    // Refetch users after assignment/unassignment
    if (refetchUsersCallback) {
      try {
        await refetchUsersCallback();
      } catch (error) {
        console.error("Error refetching users:", error);
      }
    }
  };

  const handleUnassignUser = async () => {
    if (!selectedUser || !id) return;

    setUnassignLoading(true);
    try {
      // Remove the current tenant from the user's assigned tenants
      const currentTenants = selectedUser.tenants || [];
      const updatedUser = {
        ...selectedUser,
        tenants: currentTenants.filter((tenantId) => tenantId !== id),
      };

      const response = await updateUserTenants(updatedUser);

      if (response?.success || response?.status) {
        toastRef?.current?.show({
          severity: "success",
          summary: `'${updatedUser.first_name} ${updatedUser.last_name}' has been successfully unassigned from the tenant.`,
          life: 5000,
          icon: "pi pi-check",
        });

        setDisplayUnassignUserDialog(false);
        setSelectedUser(null);
        await handleRefetchUsers();
        // Invalidate audit logs cache to refresh after unassignment
        queryClient.invalidateQueries({
          queryKey: ["tenant-user-assignments-audit", id],
        });
      }
    } catch (error) {
      console.error("Error unassigning user:", error);
      if (toastRef?.current) {
        toastRef.current?.show({
          severity: "error",
          icon: "pi pi-times-circle",
          summary: "Oops! Something went wrong while unassigning the user.",
          detail: "Please try again.",
          life: 5000,
        });
      }
    } finally {
      setUnassignLoading(false);
    }
  };

  return (
    <TenantInformationContext.Provider
      value={{
        tenant,
        setTenant,
        loading,
        activeSection,
        setActiveSection: handleSectionChange,
        fetchTenantDetails,
        onAssignUser: handleAssignUser,
        onUnAssignUser: handleUnAssignUser,
        onViewHistory: handleViewHistory,
        displayAssignUserDialog,
        setDisplayAssignUserDialog,
        displayUnassignUserDialog,
        setDisplayUnassignUserDialog,
        selectedUser,
        setSelectedUser,
        toastRef,
        setRefetchUsersCallback,
      }}
    >
      <Layout
        crumbs={[
          {
            label: <span className="breadcrumb-link">Tenants</span>,
            command: () => navigate("/tenants"),
          },
          {
            label: loading ? (
              <Skeleton width="200px" height="20px" className="mr-1" />
            ) : (
              getBreadcrumbLabel()
            ),
          },
          { label: "Core Tenant Information" },
        ]}
      >
        <div className="core-tenant-info__page">
          {loading ? (
            <Card className="gen-card h-full w-full">
              <div className="core-tenant-info__skeleton">
                <Skeleton width="30%" height="40px" className="mb-3" />
                <Skeleton width="100%" height="20px" className="mb-2" />
                <Skeleton width="100%" height="20px" className="mb-2" />
                <Skeleton width="100%" height="20px" />
              </div>
            </Card>
          ) : tenant ? (
            <div className="core-tenant-info__layout">
              <TenantSidebar
                tenant={tenant}
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
                onNavigateBack={handleNavigateBack}
              />
              <div className="core-tenant-info__main-wrapper">
                <Outlet />
              </div>
            </div>
          ) : (
            <Card className="gen-card h-full w-full">
              <div className="core-tenant-info__error">
                <p>Tenant not found</p>
              </div>
            </Card>
          )}
        </div>
        <ToastMessage ref={toastRef} />
        <AssignUserModal
          visible={displayAssignUserDialog}
          closeModal={() => setDisplayAssignUserDialog(false)}
          tenantId={id}
          toastRef={toastRef}
          fetchTenantUsers={handleRefetchUsers}
        />
        {selectedUser && (
          <UnassignModal
            visible={displayUnassignUserDialog}
            closeModal={() => {
              setDisplayUnassignUserDialog(false);
              setSelectedUser(null);
            }}
            unAssignHandler={handleUnassignUser}
            loading={unassignLoading}
            title={`Are you sure you want to unassign '${selectedUser.first_name} ${selectedUser.last_name}' from this tenant?`}
            description="This action will remove the user's access to all tenant data. Do you want to continue?"
            toastRef={toastRef}
            tenantId={id}
            fetchTenantUsers={handleRefetchUsers}
            actionBtnlabel="Yes, Unassign"
          />
        )}
      </Layout>
    </TenantInformationContext.Provider>
  );
};

export default TenantInformationLayout;
export const useTenantInformationLayout = () =>
  useContext(TenantInformationContext);
